import { buildReachDadsRoomContext } from './reachDadsRoomModel';
import { buildAdvisoryPartnersRoomContext } from './advisoryPartnersRoomModel';
import { buildGuidePartnersRoomContext } from './guidePartnersRoomModel';
import { buildSuperFansRoomContext } from './superFansRoomModel';
import { buildSupportPartnersRoomContext } from './supportPartnersRoomModel';
import { buildStewardTheWorkRoomContext } from './stewardTheWorkRoomModel';
import { buildSupportDadsRoomContext } from './supportDadsRoomModel';
import { buildSystemHealthRoomContext } from './systemHealthRoomModel';
const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const getTimeValue = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const buildToolAction = (tool, fallbackLabel = 'Start here') => {
  if (!tool?.tab) return null;
  return {
    tab: tool.tab,
    label: tool.label || fallbackLabel,
    roomId: tool.roomId || null,
  };
};

const CONTEXT_TONE_PRIORITY = {
  urgent: 3,
  attention: 2,
  next: 1,
  steady: 0,
};

const getHighestTone = (...tones) =>
  tones.reduce((selected, tone) => (
    (CONTEXT_TONE_PRIORITY[tone] ?? -1) > (CONTEXT_TONE_PRIORITY[selected] ?? -1)
      ? tone
      : selected
  ), 'steady');

const buildZoneToolAction = (tool, fallbackLabel = 'Start here') => {
  if (!tool?.tab) return null;
  return {
    tab: tool.tab,
    label: tool.label || fallbackLabel,
    roomId: tool.roomId || null,
  };
};

export function buildActiveRoomContext(room, {
  stats = null,
  founding40Status = null,
  feedbackItems = [],
  feedbackAiInsights = null,
  analytics = null,
  atRiskMembers = [],
  members = [],
  sentGuestPasses = [],
  guideSummary = null,
  emailDeliveryIssues = [],
  recordings = [],
  resources = [],
  resourceInsights = null,
  pricingCoupons = [],
  documents = [],
  siteSettings = null,
  supportOverview = null,
  advisoryOverview = null,
  superFanOverview = null,
} = {}) {
  const primaryAction = buildToolAction(room?.tools?.[0], 'Start here');
  const secondaryAction = buildToolAction(room?.tools?.[1], 'Open next tool');

  switch (room?.id) {
    case 'reach-dads':
      return buildReachDadsRoomContext({
        room,
        founding40Status,
        members,
        sentGuestPasses,
        emailDeliveryIssues,
        siteSettings,
        primaryAction,
        secondaryAction,
      });
    case 'support-dads':
      return buildSupportDadsRoomContext({
        room,
        stats,
        recordings,
        resources,
        resourceInsights,
        primaryAction,
        secondaryAction,
      });
    case 'guide-partners':
      return buildGuidePartnersRoomContext({
        room,
        guideSummary,
        emailDeliveryIssues,
        primaryAction,
        secondaryAction,
      });
    case 'advisory-partners':
      return buildAdvisoryPartnersRoomContext({
        room,
        advisoryOverview,
        primaryAction,
        secondaryAction,
      });
    case 'super-fans':
      return buildSuperFansRoomContext({
        room,
        superFanOverview,
        primaryAction,
        secondaryAction,
      });
    case 'steward-the-work':
      return buildStewardTheWorkRoomContext({
        room,
        analytics,
        atRiskMembers,
        pricingCoupons,
        primaryAction,
        secondaryAction,
      });
    case 'support-partners':
      return buildSupportPartnersRoomContext({
        room,
        supportOverview,
        primaryAction,
        secondaryAction,
      });
    case 'system-health':
      return buildSystemHealthRoomContext({
        room,
        documents,
        siteSettings,
        primaryAction,
        secondaryAction,
      });
    default:
      return {
        id: room?.id || 'room',
        statusTone: 'steady',
        statusLabel: 'Ready',
        startTitle: room?.question || 'Operate the next part of the work',
        startDetail: room?.supportingLine || '',
        signals: [],
        metrics: [],
        primaryAction,
        secondaryAction,
      };
  }
}

export function buildActiveZoneContext(zone, roomContextsById = {}, activeRoomId = null) {
  const primaryAction = buildZoneToolAction(zone?.tools?.[0], 'Start here');
  const secondaryAction = buildZoneToolAction(zone?.tools?.[1], 'Open next tool');

  if (!zone?.id) {
    return null;
  }

  if (zone.id === 'communication') {
    const reachContext = roomContextsById['reach-dads'];
    return reachContext
      ? {
          ...reachContext,
          id: zone.id,
          scope: 'zone',
          primaryAction,
          secondaryAction,
        }
      : null;
  }

  if (zone.id === 'guide-partners') {
    const guideContext = roomContextsById['guide-partners'];
    return guideContext
      ? {
          ...guideContext,
          id: zone.id,
          scope: 'zone',
          primaryAction,
          secondaryAction,
        }
      : null;
  }

  if (zone.id === 'advisory-partners') {
    const advisoryContext = roomContextsById['advisory-partners'];
    return advisoryContext
      ? {
          ...advisoryContext,
          id: zone.id,
          scope: 'zone',
          primaryAction,
          secondaryAction,
        }
      : null;
  }

  if (zone.id === 'support-partners') {
    const supportContext = roomContextsById['support-partners'];
    return supportContext
      ? {
          ...supportContext,
          id: zone.id,
          scope: 'zone',
          primaryAction,
          secondaryAction,
        }
      : null;
  }

  if (zone.id === 'super-fans') {
    const superFanContext = roomContextsById['super-fans'];
    return superFanContext
      ? {
          ...superFanContext,
          id: zone.id,
          scope: 'zone',
          primaryAction,
          secondaryAction,
        }
      : null;
  }

  if (zone.id === 'heart-work') {
    const supportContext = roomContextsById['support-dads'];
    if (!supportContext) {
      return null;
    }

    return {
      id: zone.id,
      scope: 'zone',
      statusTone: supportContext?.statusTone || 'steady',
      statusLabel: supportContext?.statusLabel || 'Steady',
      startTitle: supportContext?.startTitle || 'Stay close to what dads need and what support should move next',
      startDetail: 'Dad Partners should help you see the next real care move, the support assets that matter, and the signals that should shape both.',
      signals: (supportContext?.signals || []).slice(0, 3),
      metrics: (supportContext?.metrics || []).slice(0, 3),
      priorityItems: (supportContext?.priorityItems || []).slice(0, 3),
      watchListTitle: supportContext?.watchListTitle || 'What needs care next',
      watchListItems: (supportContext?.watchListItems || []).slice(0, 4),
      primaryAction,
      secondaryAction,
    };
  }

  if (zone.id === 'stewardship') {
    const stewardshipContext = roomContextsById['steward-the-work'];
    const systemContext = roomContextsById['system-health'];

    if (!stewardshipContext && !systemContext) {
      return null;
    }

    if (activeRoomId === 'steward-the-work' && stewardshipContext) {
      return {
        ...stewardshipContext,
        id: zone.id,
        scope: 'zone',
        primaryAction,
        secondaryAction,
      };
    }

    if (activeRoomId === 'system-health' && systemContext) {
      return {
        ...systemContext,
        id: zone.id,
        scope: 'zone',
        primaryAction,
        secondaryAction,
      };
    }

    const retentionMetric = stewardshipContext?.metrics?.[0];
    const riskMetric = stewardshipContext?.metrics?.[1];
    const offersMetric = stewardshipContext?.metrics?.[2];
    const recordsMetric = systemContext?.metrics?.[0];
    const eventsMetric = systemContext?.metrics?.[1];
    const settingsMetric = systemContext?.metrics?.[2];

    const priorityItems = [];

    if ((riskMetric?.value || 0) > 0) {
      priorityItems.push({
        tone: riskMetric.tone,
        eyebrow: 'Do first',
        title: `${pluralize(riskMetric.value, 'dad')} need retention attention`,
        detail: 'Handle retention pressure before it quietly turns into losses you could have seen coming.',
        location: 'In Analytics',
      });
    }

    if (settingsMetric?.value === 'Check') {
      priorityItems.push({
        tone: settingsMetric.tone,
        eyebrow: 'Check next',
        title: 'Review core site settings',
        detail: 'Operational settings should feel dependable before launch pressure rises.',
        location: 'In Site Settings',
      });
    }

    if ((eventsMetric?.value || 0) > 0) {
      priorityItems.push({
        tone: eventsMetric.tone,
        eyebrow: 'In motion',
        title: `${pluralize(eventsMetric.value, 'upcoming event')} already configured`,
        detail: 'The operational calendar is already taking shape, so this work mostly needs oversight.',
        location: 'In Site Settings',
      });
    }

    return {
      id: zone.id,
      scope: 'zone',
      statusTone: getHighestTone(stewardshipContext?.statusTone, systemContext?.statusTone),
      statusLabel: (riskMetric?.value || 0) > 0
        ? 'Needs attention'
        : settingsMetric?.value === 'Check'
          ? 'Needs review'
          : 'Steady',
      startTitle: (riskMetric?.value || 0) > 0
        ? 'Protect business durability first, then keep the admin foundation tidy'
        : 'Keep the mission durable by staying ahead of business risk and quiet admin drift',
      startDetail: 'Operations should make it obvious whether the business is healthy enough to sustain the work and whether the quieter admin surfaces stay dependable.',
      signals: [
        stewardshipContext?.signals?.[0],
        stewardshipContext?.signals?.[1],
        systemContext?.signals?.[0],
        systemContext?.signals?.[1],
      ].filter(Boolean).slice(0, 3),
      metrics: [retentionMetric, riskMetric, eventsMetric || settingsMetric || offersMetric || recordsMetric].filter(Boolean).slice(0, 3),
      priorityItems: priorityItems.slice(0, 3),
      watchListTitle: 'Operational watchlist',
      watchListItems: [
        settingsMetric?.value === 'Check'
          ? { title: 'Site settings still need review', detail: 'Core configuration is not yet fully settled.', tone: settingsMetric.tone }
          : null,
        (eventsMetric?.value || 0) > 0
          ? { title: `${pluralize(eventsMetric.value, 'upcoming event')} scheduled`, detail: 'Keep logistics and readiness from slipping.', tone: eventsMetric.tone }
          : null,
        (offersMetric?.value || 0) > 0
          ? { title: `${pluralize(offersMetric.value, 'active offer')} still live`, detail: 'Pricing levers are active and should stay intentional.', tone: offersMetric.tone }
          : null,
      ].filter(Boolean),
      primaryAction,
      secondaryAction,
    };
  }

  return null;
}
